import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import Icon from 'components/Utils/NewIcon';
import UIUtils from 'services/UIUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import SelectableListItem from 'components/Utils/SelectableListItem';
import DefaultButton from 'components/Utils/NewDefaultButton';
import NewContact from 'components/Contacts/NewContact';
import ContactBadge from 'components/Contacts/ContactBadge';
import './style.scss';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultReactSelect from 'components/Utils/DefaultReactSelect';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { GetStaff } from 'careand-redux/selectors';
import ProgressMessage from 'components/Utils/ProgressMessage';
import IconButton from '@material-ui/core/IconButton';
import DefaultModal from 'components/Utils/DefaultModal';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import GoogleMap from 'components/Utils/GoogleMap';
import MiscUtils from 'services/MiscUtils';
import ResponsiveColumns from 'components/ResponsiveColumns';
import { useMediaQuery } from '@material-ui/core';
import ConfirmationButton from '../../Utils/NewDefaultButton/ConfirmationButton';

const ContactsSearch = (props) => {
  const dispatch = useAppDispatch();
  const physician = useAppSelector(GetStaff);

  const navigateTo = useRef();

  const [typingTimeout, setTypingTimeout] = useState();
  const [addConsultant, setAddConsultant] = useState(!!props.addScreen);
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState([]);
  const [clickedItem, setClickedItem] = useState(props.contact);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const searching = useIsLoading(api.Contacts.getPhysicianContacts);

  const favorite_contacts = physician?.favorite_contacts || [];

  useEffect(() => {
    getContacts('', 50);
  }, []);

  function enterPressed(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      clearTimeout(typingTimeout);
      let value = searchTerm;
      if (value !== undefined && value !== '') {
        getContacts(value);
      }
    }
  }

  function onChangeAddNew(contact) {
    let search = contact?.name;
    if (search) handleSearchChange(undefined, search, false);
  }

  function handleSearchChange(event, inputValue, saveSearch = true) {
    clearTimeout(typingTimeout);
    let value = inputValue || event?.target?.value || '';

    let timeout = setTimeout(() => {
      if (value !== undefined && value !== '') getContacts(value);
    }, UIUtils.SEARCH_TYPE_TIMEOUT);

    if (saveSearch) setSearchTerm(value);
    setTypingTimeout(timeout);
  }

  async function getContacts(value, limit = mapOpen ? 50 : 30) {
    let contacts = await dispatch(api.Contacts.getPhysicianContacts({ name: value, limit }));
    if (contacts) {
      let favorites = contacts.filter(({ id }) => favorite_contacts.includes(id));
      let nonFavorites = contacts.filter(({ id }) => !favorite_contacts.includes(id));
      setContacts([...favorites, ...nonFavorites]);
    }
  }

  async function deleteContact(value) {
    let originalContacts = [...contacts];
    let newContacts = PerformanceUtils.removeFromArray(value, contacts);
    setClickedItem();
    setContacts(newContacts);
    const { isSuccess } = await dispatch(api.Contacts.removePhysicianContact({ id: value.id }), {
      errorTitle: 'Delete Contact',
      meta: true,
    });
    if (isSuccess) {
      setContacts(originalContacts);
    }
  }

  function selectItem(item, confirm = false) {
    setClickedItem(item);
    setContacts(PerformanceUtils.editOrAdd(item, contacts));
    setAddConsultant(false);
    if (props.fullscreen) {
      setTimeout(() => navigateTo.current?.(1), 0);
    }
    if (confirm && props.onSelect) {
      props.onSelect(item);
    }
  }

  if (addConsultant && !props.fullscreen) {
    return <NewContact onSuccess={selectItem} contact={clickedItem} />;
  }

  let list = contacts.map((contact) => {
    let selected = !!clickedItem && clickedItem.id === contact.id;
    return (
      <SelectableListItem
        key={contact.id}
        selected={selected}
        onClick={() => selectItem(contact)}
        item={<ContactBadge contact={contact} />}
      />
    );
  });

  const buttons = (
    <div className="buttons">
      {!!clickedItem && (
        <>
          <ConfirmationButton
            icon="trash"
            className="red"
            confirmation={{
              message: 'Are you sure you want to delete this contact?',
            }}
            style={{ width: '25px' }}
            onClick={() => deleteContact(clickedItem)}
          />
          {!props.fullscreen && (
            <DefaultButton
              icon="pencil"
              style={{ width: '25px', marginLeft: '10px' }}
              onClick={() => setAddConsultant(true)}
            />
          )}
        </>
      )}
      {!!props.onSelect && (
        <DefaultButton
          name="Select"
          className="blue"
          style={{ float: 'right', width: '100px' }}
          disabled={!clickedItem}
          onClick={() => props.onSelect(clickedItem)}
        />
      )}
    </div>
  );

  let className = 'contacts-search';
  if (props.fullscreen) className += ' fullscreen';

  return (
    <>
      {mapOpen && (
        <DefaultModal
          className="contacts-map-modal large-modal"
          title="Contacts Map"
          wrap={true}
          padding={false}
          onRequestClose={(event) => setMapOpen(false)}
          content={
            <ContactsMap
              patient={props.patient}
              loading={searching}
              contacts={contacts}
              onSearchChange={(search) => handleSearchChange(undefined, search)}
              search={searchTerm || ''}
              onSelect={(contact) => {
                if (contact) {
                  setClickedItem(contact);
                  if (props.onSelect) props.onSelect(contact);
                  setMapOpen(false);
                }
              }}
            />
          }
          isOpen={true}
        />
      )}
      <div className={className}>
        {!!props.inlineSearch && (
          <DefaultReactSelect
            className="contacts-search-field"
            name="search"
            placeholder="Contacts Search"
            inputValue={searchTerm || ''}
            menuIsOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            filterOption={() => true}
            loading={searching}
            noOptionsMessage="No contact found"
            onChange={(data) => setClickedItem(data.value)}
            onInputChange={(input) => handleSearchChange(undefined, input)}
            options={contacts.map((contact) => {
              return {
                value: contact,
              };
            })}
            components={{
              Option: ({ data, innerRef, isFocused }) => {
                return (
                  <SelectableListItem
                    divider={true}
                    buttonRef={innerRef}
                    key={data.value.id}
                    onClick={() => {
                      setClickedItem(data.value);
                      if (props.onSelect) props.onSelect(data.value);
                      setMenuOpen(false);
                    }}
                    item={<ContactBadge contact={data.value} />}
                  />
                );
              },
            }}
          />
        )}
        {!!props.inlineSearch && props.patient && (
          <IconButton
            className="blue"
            onClick={() => setMapOpen(true)}
            style={{ width: '35px', height: '35px' }}
          >
            <Icon src="location-dot" className="white" alt="Search in map" showTooltip={true} />
          </IconButton>
        )}
        {!props.inlineSearch && (
          <>
            <ResponsiveColumns
              navigateTo={navigateTo}
              columns={[
                {
                  name: 'Contacts',
                  element: (
                    <div className="search-column  full-height-panel scroll-list underside-right-panel">
                      <div className="search-field">
                        {!searching && <Icon src="magnifying-glass" />}
                        <ProgressMessage size={20} show={searching} />
                        <DefaultTextField
                          name="searchTerm"
                          autoFocus={true}
                          label="Search by name"
                          value={searchTerm || ''}
                          onKeyDown={enterPressed}
                          onChange={handleSearchChange}
                        />
                        {props.patient && (
                          <IconButton
                            className="blue"
                            onClick={() => setMapOpen(true)}
                            style={{ width: '35px', height: '35px' }}
                          >
                            <Icon src="location-dot" className="white" />
                          </IconButton>
                        )}
                        {!props.hideAddButton && (
                          <IconButton
                            className="blue"
                            onClick={() => {
                              setClickedItem();
                              setAddConsultant(true);
                              if (props.fullscreen) {
                                setTimeout(() => navigateTo.current?.(1), 0);
                              }
                            }}
                            style={{ width: '35px', height: '35px', marginLeft: '8px' }}
                          >
                            <Icon src="plus" className="white" />
                          </IconButton>
                        )}
                      </div>
                      <div className="list" style={{ overflowY: 'auto' }}>
                        <List>{list}</List>
                      </div>
                      {!props.fullscreen && { buttons }}
                    </div>
                  ),
                },
                (!!clickedItem || addConsultant) && !!props.fullscreen
                  ? {
                      name: 'Details',
                      element: (
                        <div className="details-panel full-height scroll-list">
                          <NewContact
                            key={(clickedItem || {}).id || 'new'}
                            onSuccess={selectItem}
                            contact={clickedItem}
                            buttons={!!props.fullscreen ? buttons : null}
                            onChange={onChangeAddNew}
                          />
                        </div>
                      ),
                    }
                  : undefined,
              ]}
            />
          </>
        )}
      </div>
    </>
  );
};

const google = window.google;

const ContactsMap = ({ patient, input, contacts, onSearchChange, loading, onSelect }) => {
  const isPhone = useMediaQuery('(max-width: 767px)');
  const dispatch = useAppDispatch();
  const clinic = useAppSelector((state) => state.resource.clinic);
  const listContainerRef = useRef(null);
  const navigateTo = useRef();
  const [centerLatLng, setCenterLatLng] = useState(null);

  const [clickedContact, setClickedContact] = useState(null);

  useEffect(() => {
    if (!patient) {
      return;
    }
    if (!centerLatLng) {
      if (!patient.address?.latitude) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
          { address: MiscUtils.getFullAddress(patient.address) },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) {
              let location = results[0].geometry.location;
              setCenterLatLng(location);
              savePatientLatLng(location.lat(), location.lng());
            } else {
              setCenterLatLng(getLatLng(clinic.address));
            }
          },
        );
      } else {
        setCenterLatLng(getLatLng(patient.address));
      }
    }
  }, [patient, clinic, centerLatLng]);

  useEffect(() => {
    setClickedContact();
  }, []);

  async function savePatientLatLng(lat, lng) {
    let newPatient = {
      ...patient,
      address: { ...patient.address, latitude: `${lat}`, longitude: `${lng}` },
    };
    await dispatch(api.Patients.updatePatient({ patient: newPatient, id: patient.id }));
  }

  function getLatLng(address) {
    if (!!address?.latitude && !!address?.longitude) {
      return new google.maps.LatLng({
        lat: parseFloat(address.latitude),
        lng: parseFloat(address.longitude),
      });
    }
  }

  const panTo = useMemo(() => {
    let address = clickedContact?.address;
    if (address) {
      return getLatLng(address);
    }
  }, [clickedContact]);

  const contactLatLngs = useMemo(() => {
    return contacts
      .map((contact) => {
        if (contact?.address) {
          return getLatLng(contact.address);
        }
      })
      .filter(Boolean);
  }, [contacts, clickedContact, panTo]);

  const getInfo = useCallback(
    (latLng) => {
      let matchingContact = contacts.find((contact) => {
        const address = contact.address;
        if (!address) return false;
        return `${latLng.lat()}` === address.latitude && `${latLng.lng()}` === address.longitude;
      });
      if (matchingContact) {
        let selected = matchingContact?.id === clickedContact?.id;
        return getContentInfoString(matchingContact, selected);
      }
    },
    [contacts, clickedContact],
  );

  const onMarkerClick = useCallback(
    (latLng) => {
      let matchingContact = contacts.find((contact) => {
        const address = contact.address;
        if (!address) return false;
        return `${latLng.lat()}` === address.latitude && `${latLng.lng()}` === address.longitude;
      });
      if (matchingContact) {
        setClickedContact(matchingContact);
        if (!isPhone) {
          setTimeout(() => {
            const selectedElement = listContainerRef.current?.querySelector(
              `[data-contact-id="${matchingContact.id}"]`,
            );
            if (selectedElement) {
              selectedElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }, 0);
        }
      }
    },
    [contacts, isPhone],
  );

  function getContentInfoString(contact, selected) {
    const { name, specialty, email, phone, fax, notes, alert } = contact;
    let className = selected ? 'selected' : '';
    return `<div id="content" class="${className}">
        <h1 id="firstHeading" class="firstHeading bold">${name}${
          specialty ? ` (${specialty})` : ''
        }</h1>
        <div id="bodyContent">
          ${email ? `<p>Email: ${email}</p>` : ''}
          ${phone ? `<p>Phone: ${phone}</p>` : ''}
          ${fax ? `<p>Fax: ${fax}</p>` : ''}
          ${notes ? `<p>Notes: ${notes}</p>` : ''}
          ${alert ? `<p>Alert: ${alert}</p>` : ''}
        </div>
      </div>`;
  }

  function selectContact(contact) {
    setClickedContact(contact);
  }

  return (
    <div className="contacts-map">
      <ResponsiveColumns
        navigateTo={navigateTo}
        columns={[
          {
            name: 'Search',
            element: (
              <div className="contacts-list">
                <div className="input-bar">
                  <Icon
                    src="magnifying-glass"
                    height="15px"
                    width="15px"
                    className="search-icon"
                    loading={loading}
                  />
                  <DefaultTextField
                    name="search"
                    label="Contacts Search"
                    value={input}
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                </div>
                <div className="list" style={{ overflowY: 'auto' }} ref={listContainerRef}>
                  <List>
                    {contacts.map((contact) => {
                      let selected = clickedContact?.id === contact.id;
                      return (
                        <div data-contact-id={contact.id}>
                          <SelectableListItem
                            key={contact.id}
                            selected={selected}
                            onClick={() => selectContact(contact)}
                            item={
                              <ContactBadge
                                contact={contact}
                                buttons={
                                  <div
                                    className="check"
                                    style={{ position: 'absolute', right: 0, bottom: '20px' }}
                                  >
                                    {selected && (
                                      <Icon
                                        src="circle-check"
                                        width="30px"
                                        fontAwesomeStyle="fad"
                                        className="blue"
                                        showTooltip={true}
                                        alt="Select Contact"
                                        iconStyle={{
                                          '--fa-primary-color': 'white',
                                          '--fa-secondary-color': '#2693D1',
                                          '--fa-secondary-opacity': 1.0,
                                        }}
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          onSelect(contact);
                                        }}
                                      />
                                    )}
                                  </div>
                                }
                              />
                            }
                          />
                        </div>
                      );
                    })}
                  </List>
                </div>
              </div>
            ),
          },
          {
            name: 'Map',
            element: (
              <div className="google-map-wrapper" style={{ width: '100%', height: '100%' }}>
                <GoogleMap
                  currentPosition={centerLatLng}
                  center={centerLatLng}
                  panTo={panTo}
                  latLngs={contactLatLngs}
                  getInfo={getInfo}
                  onClick={onMarkerClick}
                  zoom={13}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

ContactsSearch.propTypes = {
  onSelect: PropTypes.func,
  patient: PropTypes.object,
  addScreen: PropTypes.bool,
  hideAddButton: PropTypes.bool,
  contact: PropTypes.object,
};

export default ContactsSearch;
